import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  CheckCircle as CheckCircleSuccessIcon,
  Error as ErrorIcon,
} from "@mui/icons-material";
import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/apiClient";
import AuthLayout from "../components/AuthLayout";

import type { Theme } from "@mui/material/styles";

const getInputSx = (theme: Theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "1rem",
    "& fieldset": { borderColor: theme.palette.divider, borderWidth: "2px" },
    "&:hover fieldset": { borderColor: theme.palette.text.secondary },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "2px",
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
    "&.Mui-disabled fieldset": { borderColor: theme.palette.divider, opacity: 0.5 },
  },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiInputBase-input": {
    padding: "0.75rem 1rem",
    "&::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: theme.palette.text.disabled,
  },
});

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const passwordRequirements: PasswordRequirement[] = useMemo(
    () => [
      {
        label: t("resetPassword.requirements.minLength", "Mínimo 8 caracteres"),
        test: (pwd) => pwd.length >= 8,
      },
      {
        label: t("resetPassword.requirements.uppercase", "Uma letra maiúscula"),
        test: (pwd) => /[A-Z]/.test(pwd),
      },
      {
        label: t("resetPassword.requirements.lowercase", "Uma letra minúscula"),
        test: (pwd) => /[a-z]/.test(pwd),
      },
      {
        label: t("resetPassword.requirements.number", "Um número"),
        test: (pwd) => /\d/.test(pwd),
      },
    ],
    [t],
  );

  const passwordStrength = useMemo(() => {
    const passed = passwordRequirements.filter((req) => req.test(newPassword)).length;
    return (passed / passwordRequirements.length) * 100;
  }, [newPassword, passwordRequirements]);

  const isPasswordValid = passwordStrength === 100;
  const doPasswordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    if (!token) {
      setError(
        t("resetPassword.errors.missingToken", "Token de recuperação não encontrado. Solicite um novo link."),
      );
    }
  }, [token, t]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!token) {
      setError(t("resetPassword.errors.missingToken", "Token de recuperação não encontrado."));
      return;
    }
    if (!isPasswordValid) {
      setError(t("resetPassword.errors.weakPassword", "A senha não atende aos requisitos mínimos."));
      return;
    }
    if (!doPasswordsMatch) {
      setError(t("resetPassword.errors.passwordMismatch", "As senhas não coincidem."));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await api.resetPassword(token, newPassword);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      setError(
        err?.message ||
          t("resetPassword.errors.generic", "Erro ao redefinir senha. O link pode ter expirado."),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthColor = (): "error" | "warning" | "success" => {
    if (passwordStrength < 50) return "error";
    if (passwordStrength < 100) return "warning";
    return "success";
  };

  const getStrengthLabel = (): string => {
    if (passwordStrength < 50) return t("resetPassword.strength.weak", "Fraca");
    if (passwordStrength < 100) return t("resetPassword.strength.medium", "Média");
    return t("resetPassword.strength.strong", "Forte");
  };

  return (
    <AuthLayout
      title={t("resetPassword.title", "Redefinir senha")}
      subtitle={t("resetPassword.subtitle", "Crie uma nova senha segura para sua conta.")}
    >
      {!success ? (
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: "8px" }}>
                {error}
              </Alert>
            )}

            <Box>
              <Stack spacing={0.5}>
                <Typography
                  variant="body2"
                sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: "0.9375rem" }}
                >
                  {t("resetPassword.newPassword", "Nova senha")}
                </Typography>
                <TextField
                  placeholder={t("resetPassword.newPasswordPlaceholder", "Mínimo 8 caracteres")}
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  autoFocus
                  disabled={isSubmitting || !token}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: 36,
                            height: 36,
                            border: "none",
                            background: "none",
                            cursor: "pointer",
                            padding: 0,
                            color: "inherit",
                          }}
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </button>
                      </InputAdornment>
                    ),
                  }}
                  sx={getInputSx(theme)}
                />
              </Stack>

              {newPassword && (
                <Box sx={{ mt: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {t("resetPassword.strength.label", "Força da senha")}
                    </Typography>
                    <Typography variant="caption" fontWeight={600} color={`${getStrengthColor()}.main`}>
                      {getStrengthLabel()}
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={passwordStrength}
                    color={getStrengthColor()}
                    sx={{ borderRadius: 1, height: 4 }}
                  />
                </Box>
              )}
            </Box>

            {newPassword && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: "8px",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                }}
              >
                <Stack spacing={0.5}>
                  {passwordRequirements.map((req, index) => {
                    const passed = req.test(newPassword);
                    return (
                      <Stack key={index} direction="row" alignItems="center" spacing={0.75}>
                        {passed ? (
                          <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />
                        ) : (
                          <ErrorIcon sx={{ fontSize: 14, color: "text.disabled" }} />
                        )}
                        <Typography
                          variant="caption"
                          color={passed ? "success.main" : "text.secondary"}
                          fontSize="0.75rem"
                        >
                          {req.label}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            )}

            <Stack spacing={0.5}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#757575", fontSize: "0.9375rem" }}
              >
                {t("resetPassword.confirmPassword", "Confirmar senha")}
              </Typography>
              <TextField
                placeholder={t("resetPassword.confirmPasswordPlaceholder", "Repita a senha")}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                disabled={isSubmitting || !token}
                fullWidth
                error={confirmPassword.length > 0 && !doPasswordsMatch}
                helperText={
                  confirmPassword.length > 0 && !doPasswordsMatch
                    ? t("resetPassword.errors.passwordMismatch", "As senhas não coincidem")
                    : ""
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 36,
                          height: 36,
                          border: "none",
                          background: "none",
                          cursor: "pointer",
                          padding: 0,
                          color: "inherit",
                        }}
                      >
                        {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </button>
                    </InputAdornment>
                  ),
                }}
                sx={getInputSx(theme)}
              />
            </Stack>

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isSubmitting || !token || !isPasswordValid || !doPasswordsMatch}
              fullWidth
              startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: "1rem",
                textTransform: "none",
                background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
                boxShadow: "0 4px 14px rgba(21, 101, 192, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                  boxShadow: "0 6px 20px rgba(21, 101, 192, 0.4)",
                },
                "&:disabled": {
                  background: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.12)"
                      : "rgba(0,0,0,0.12)",
                  boxShadow: "none",
                },
              }}
            >
              {isSubmitting
                ? t("resetPassword.saving", "Salvando...")
                : t("resetPassword.submit", "Redefinir senha")}
            </Button>

            <Button
              component={Link}
              to="/login"
              variant="text"
              size="small"
              sx={{ textTransform: "none", fontWeight: 500, fontSize: "0.8125rem", color: theme.palette.text.secondary }}
            >
              {t("resetPassword.backToLogin", "Voltar ao login")}
            </Button>
          </Stack>
        </Box>
      ) : (
        <Stack spacing={3} alignItems="center" py={2}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 14px rgba(76, 175, 80, 0.3)",
            }}
          >
            <CheckCircleSuccessIcon sx={{ fontSize: 36, color: "#fff" }} />
          </Box>

          <Typography variant="h6" fontWeight={700} textAlign="center">
            {t("resetPassword.successTitle", "Senha redefinida!")}
          </Typography>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            {t(
              "resetPassword.successMessage",
              "Sua senha foi atualizada com sucesso. Você será redirecionado para o login.",
            )}
          </Typography>

          <CircularProgress size={20} sx={{ mt: 1 }} />

          <Button
            component={Link}
            to="/login"
            variant="contained"
            fullWidth
            sx={{
              py: 1.5,
              mt: 1,
              borderRadius: "8px",
              fontWeight: 600,
              fontSize: "1rem",
              textTransform: "none",
              background: "linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)",
              boxShadow: "0 4px 14px rgba(21, 101, 192, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
                boxShadow: "0 6px 20px rgba(21, 101, 192, 0.4)",
              },
            }}
          >
            {t("resetPassword.goToLogin", "Ir para o login")}
          </Button>
        </Stack>
      )}
    </AuthLayout>
  );
}
